import { useState } from 'react';
import { apiClient } from '../lib/api-client';

interface BreakdownItem {
  char: string;
  pinyin?: string;
  accepted_chars?: string[];
  accepted?: string[];
  reading?: string;
}

interface RegexResponse {
  token: string;
  regex: string;
  flags?: string;
  breakdown?: BreakdownItem[];
}

interface BatchResponse {
  results: RegexResponse[];
}

interface LangOption {
  code: string;
  label: string;
  needsSentence?: boolean;
}

const LANG_GROUPS: { heading: string; langs: LangOption[] }[] = [
  {
    heading: 'CJK',
    langs: [
      { code: 'cmn_tw', label: 'Mandarin TW', needsSentence: true },
      { code: 'cmn_cn', label: 'Mandarin CN', needsSentence: true },
      { code: 'nan_tw', label: 'Taiwanese' },
      { code: 'hak_sx', label: 'Hakka' },
      { code: 'yue_hk', label: 'Cantonese' },
      { code: 'jpn', label: 'Japanese', needsSentence: true },
      { code: 'kor', label: 'Korean' },
    ],
  },
  {
    heading: 'Latin',
    langs: [
      { code: '', label: '(auto-detect)' },
      { code: 'eng', label: 'English' },
      { code: 'fra', label: 'French' },
      { code: 'spa', label: 'Spanish' },
      { code: 'cat', label: 'Catalan' },
      { code: 'ita', label: 'Italian' },
      { code: 'por', label: 'Portuguese' },
      { code: 'deu', label: 'German' },
      { code: 'nld', label: 'Dutch' },
      { code: 'ron', label: 'Romanian' },
      { code: 'ces', label: 'Czech' },
      { code: 'slk', label: 'Slovak' },
      { code: 'slv', label: 'Slovene' },
      { code: 'hrv', label: 'Croatian' },
      { code: 'pol', label: 'Polish' },
      { code: 'hun', label: 'Hungarian' },
      { code: 'lav', label: 'Latvian' },
      { code: 'lit', label: 'Lithuanian' },
      { code: 'ekk', label: 'Estonian' },
      { code: 'fin', label: 'Finnish' },
      { code: 'dan', label: 'Danish' },
      { code: 'swe', label: 'Swedish' },
      { code: 'nor', label: 'Norwegian' },
      { code: 'isl', label: 'Icelandic' },
      { code: 'cym', label: 'Welsh' },
      { code: 'gle', label: 'Irish' },
      { code: 'glv', label: 'Manx' },
      { code: 'gla', label: 'Scottish Gaelic' },
      { code: 'tur', label: 'Turkish' },
      { code: 'aze', label: 'Azerbaijani' },
      { code: 'vie', label: 'Vietnamese' },
      { code: 'ind', label: 'Indonesian' },
      { code: 'tgl', label: 'Tagalog' },
      { code: 'swh', label: 'Swahili' },
    ],
  },
  {
    heading: 'Cyrillic',
    langs: [
      { code: 'rus', label: 'Russian' },
      { code: 'ukr', label: 'Ukrainian' },
      { code: 'bel', label: 'Belarusian' },
      { code: 'bul', label: 'Bulgarian' },
      { code: 'srp', label: 'Serbian' },
      { code: 'kaz', label: 'Kazakh' },
      { code: 'uzn', label: 'Uzbek' },
      { code: 'khk', label: 'Mongolian' },
    ],
  },
  {
    heading: 'Greek',
    langs: [{ code: 'ell', label: 'Greek' }],
  },
  {
    heading: 'Other scripts',
    langs: [
      { code: 'hye', label: 'Armenian' },
      { code: 'kat', label: 'Georgian' },
      { code: 'arb', label: 'Arabic (MSA)' },
      { code: 'ary', label: 'Moroccan Arabic' },
      { code: 'arz', label: 'Egyptian Arabic' },
      { code: 'pes', label: 'Persian' },
      { code: 'heb', label: 'Hebrew' },
      { code: 'ckb', label: 'Sorani' },
      { code: 'hin', label: 'Hindi' },
      { code: 'ben', label: 'Bengali' },
      { code: 'tha', label: 'Thai' },
    ],
  },
];

const ALL_LANGS = LANG_GROUPS.flatMap((g) => g.langs);

const CJK_CODES = new Set(['cmn_tw', 'cmn_cn', 'jpn', 'kor']);

function isSentenceInput(text: string, langCode: string): boolean {
  if (CJK_CODES.has(langCode)) return false;
  return /\s/.test(text.trim());
}

export default function Regex() {
  const [langCode, setLangCode] = useState('');
  const [token, setToken] = useState('');
  const [sentence, setSentence] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [result, setResult] = useState<RegexResponse | null>(null);
  const [batchResults, setBatchResults] = useState<RegexResponse[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = ALL_LANGS.find((l) => l.code === langCode);
  const needsSentence = selected?.needsSentence === true;
  const inputIsSentence = isSentenceInput(token, langCode);

  async function generate() {
    const t = token.trim();
    const s = sentence.trim();
    if (!t) { setError('Enter a token or sentence'); return; }
    if (needsSentence && !s) { setError('Sentence required for this language'); return; }

    setLoading(true);
    setError(null);
    setResult(null);
    setBatchResults(null);

    try {
      if (langCode === 'cmn_tw' || langCode === 'cmn_cn') {
        const data = await apiClient<RegexResponse>('/api/regex/chinese', {
          method: 'POST',
          body: JSON.stringify({
            token: t,
            sentence: s,
            variant: langCode === 'cmn_tw' ? 'TW' : 'CN',
          }),
        });
        setResult(data);
      } else if (inputIsSentence) {
        const payload: Record<string, unknown> = {
          tokenized_sentence: t,
          caseSensitive,
        };
        if (langCode) payload.language = langCode;
        payload.sentence = t;
        const data = await apiClient<BatchResponse>('/api/regex', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setBatchResults(data.results);
      } else {
        const payload: Record<string, unknown> = { token: t, caseSensitive };
        if (langCode) payload.language = langCode;
        if (s) payload.sentence = s;
        const data = await apiClient<RegexResponse>('/api/regex', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setResult(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-zinc-100 font-heading">
          Regex Generator
        </h1>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap gap-3">
          <div className="w-full sm:w-56">
            <label className="block text-xs text-zinc-500 mb-1">Language</label>
            <select
              value={langCode}
              onChange={(e) => setLangCode(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200 w-full"
            >
              {LANG_GROUPS.map((g) => (
                <optgroup key={g.heading} label={g.heading}>
                  {g.langs.map((l) => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="flex items-end pb-0.5">
            <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={caseSensitive}
                onChange={(e) => setCaseSensitive(e.target.checked)}
                className="rounded border-zinc-600 bg-zinc-800 text-accent-teal focus:ring-accent-teal"
              />
              Case-sensitive
            </label>
          </div>
        </div>

        <div>
          <label className="block text-xs text-zinc-500 mb-1">
            Token or sentence
          </label>
          <input
            type="text"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') generate(); }}
            placeholder="e.g. 132, that's, or a full sentence"
            className="bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-zinc-200 text-lg w-full"
          />
          {inputIsSentence && (
            <p className="text-xs text-zinc-500 mt-1">
              Sentence detected — will generate regex for each word
            </p>
          )}
        </div>

        {needsSentence && (
          <div>
            <label className="block text-xs text-zinc-500 mb-1">
              Sentence (required for CJK — polyphone context)
            </label>
            <input
              type="text"
              value={sentence}
              onChange={(e) => setSentence(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') generate(); }}
              placeholder="Full sentence containing the token"
              className="bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-zinc-200 w-full"
            />
          </div>
        )}

        <button
          onClick={generate}
          disabled={loading}
          className="px-4 py-2 rounded text-sm font-medium bg-accent-teal/20 text-accent-teal border border-accent-teal/30 hover:bg-accent-teal/30 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Generating...' : 'Generate Regex'}
        </button>
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      {loading && (
        <p className="text-zinc-500 text-sm">
          {needsSentence ? 'Calling Claude API for pronunciation...' : 'Generating regex...'}
        </p>
      )}

      {batchResults && (
        <div className="rounded border border-accent-teal/40 bg-accent-teal/5 p-4 space-y-1">
          {(() => {
            const anyI = batchResults.some(
              (r) => (r.flags || '').includes('i') || r.regex.startsWith('(?i)'),
            );
            const combined =
              (anyI ? '(?i)' : '') +
              batchResults.map((r) => r.regex.replace(/^\(\?i\)/, '')).join(' *');
            return (
              <div className="mb-3">
                <div className="text-xs text-zinc-500 mb-1">
                  Combined sentence regex{' '}
                  <span className="text-zinc-600">
                    (tokens joined with{' '}
                    <code className="text-zinc-400 font-mono">{' *'}</code> — zero or more
                    spaces)
                  </span>
                </div>
                <code className="block text-sm text-emerald-300 font-mono break-all bg-zinc-900/60 rounded p-2 border border-zinc-800">
                  {combined}
                </code>
              </div>
            );
          })()}
          <div className="text-xs text-zinc-500 mb-2">
            {batchResults.length} tokens processed
          </div>
          <div className="overflow-x-auto">
            <table className="text-sm min-w-[320px] w-full">
              <thead>
                <tr className="text-zinc-500 text-xs">
                  <th className="text-left pr-4 pb-1">Token</th>
                  <th className="text-left pr-4 pb-1">Regex</th>
                  <th className="text-left pb-1">Flags</th>
                </tr>
              </thead>
              <tbody>
                {batchResults.map((item, j) => (
                  <tr key={j} className="border-t border-zinc-800">
                    <td className="pr-4 py-1.5 text-zinc-200 font-medium whitespace-nowrap">
                      {item.token}
                    </td>
                    <td className="pr-4 py-1.5">
                      <code className="text-sm text-amber-300 font-mono break-all">
                        {item.regex}
                      </code>
                    </td>
                    <td className="py-1.5 text-zinc-500 font-mono text-xs">
                      {item.flags || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div className="rounded border border-accent-teal/40 bg-accent-teal/5 p-4 space-y-3">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-xl text-zinc-100">{result.token}</span>
            <code className="text-sm text-amber-300 font-mono break-all">
              {result.regex}
            </code>
            {result.flags && (
              <span className="text-xs text-zinc-500 font-mono">
                flags: /{result.flags}/
              </span>
            )}
          </div>

          {result.breakdown && result.breakdown.length > 0 && (
            <div className="overflow-x-auto">
              <table className="text-sm min-w-[320px]">
                <thead>
                  <tr className="text-zinc-500 text-xs">
                    <th className="text-left pr-4 pb-1">Char</th>
                    {result.breakdown[0]?.pinyin !== undefined && (
                      <th className="text-left pr-4 pb-1">Pinyin</th>
                    )}
                    {result.breakdown[0]?.reading !== undefined && (
                      <th className="text-left pr-4 pb-1">Reading</th>
                    )}
                    <th className="text-left pb-1">Accepted</th>
                  </tr>
                </thead>
                <tbody>
                  {result.breakdown.map((item, j) => (
                    <tr key={j} className="border-t border-zinc-800">
                      <td className="pr-4 py-1 text-zinc-200 text-lg">{item.char}</td>
                      {item.pinyin !== undefined && (
                        <td className="pr-4 py-1 font-mono text-accent-teal">{item.pinyin}</td>
                      )}
                      {item.reading !== undefined && (
                        <td className="pr-4 py-1 font-mono text-accent-teal">{item.reading}</td>
                      )}
                      <td className="py-1 text-zinc-400">
                        {(item.accepted || item.accepted_chars || []).join(' ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
